import { useMemo, useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

import { MetricOverrideDialog } from '@/app/(app)/analytics/call-analytics/[id]/components/metric-override-dialog';
import {
  BooleanMetricStatus,
  buildMetricStatus,
  ListMetricStatus,
  NumberMetricStatus,
  StringMetricStatus,
} from '@/app/(app)/analytics/call-analytics/components/metric-status';
import { Popover } from '@/components/atoms/popover';
import { ApiSchemas } from '@/components/providers/v1-api';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type MetricType = 'string' | 'list_of_strings' | 'boolean' | 'number';

type MetricValue = ApiSchemas['ExternalCallMetric'];

export function toReadableName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export interface MetricCardProps {
  callId: string;
  metricKey: string;
  metric: MetricValue;
  className?: string;
  onClick?: () => void;
  isEditing?: boolean;
}

export default function MetricCard({ callId, metricKey, metric, className, onClick, isEditing }: MetricCardProps) {
  const [showReasoning, setShowReasoning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const readableName = useMemo(() => {
    return toReadableName(metric?.label ?? metricKey);
  }, [metric?.label, metricKey]);

  const metricStatus = useMemo(() => {
    return buildMetricStatus(metric);
  }, [metric]);

  const status = simpleStatusFor(metricStatus);

  const isNA = useMemo(() => {
    if (metricStatus?.kind === 'number') {
      const numStatus = metricStatus as NumberMetricStatus;
      return Math.round(numStatus.value ?? 0) === -1;
    }
    return false;
  }, [metricStatus]);

  const { showProgress, progressPercent, progressColor } = useMemo(() => {
    if (metricStatus?.kind === 'number' && !isNA) {
      const numStatus = metricStatus as NumberMetricStatus;
      const percent = Math.round(numStatus.percent ?? 0);

      let color = 'bg-primary/70';
      if (status === 'warn') color = 'bg-amber-400';
      if (status === 'bad') color = 'bg-red-400';
      if (status === 'ok') color = 'bg-green-400';

      return { showProgress: true, progressPercent: percent, progressColor: color };
    }
    return { showProgress: false, progressPercent: 0, progressColor: 'bg-primary/70' };
  }, [metricStatus, status, isNA]);

  const showMetadata = !isNA && !metric?.override && (metric?.confidence || metric?.justify);

  return (
    <div
      onClick={onClick}
      className={cn('relative flex flex-col gap-2 rounded-xl border-[0.5px] p-3 shadow-sm', className)}
      role='group'
    >
      {/* Top row: title + pill + optional status icon */}
      <div className='flex items-center justify-between gap-2'>
        <h4 className='text-sm leading-tight font-semibold'>{readableName}</h4>
        <div className='flex items-start gap-1'>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDialogOpen(true);
            }}
            className='group/pill relative'
          >
            <div
              className={cn(
                'inline-flex min-h-[1.5rem] items-center justify-center rounded-full px-2 py-1 transition-colors',
                metric?.override ? 'bg-blue-50 dark:bg-blue-950/30' : 'bg-muted'
              )}
            >
              <PillContent result={metric} isNA={isNA} />
            </div>
            <div className='absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover/pill:opacity-100'>
              <Pencil className='h-3 w-3 text-white' />
            </div>
          </button>
        </div>
      </div>

      {/* Progress for numeric */}
      {showProgress && (
        <div className='relative h-1.5 w-full overflow-hidden rounded-full bg-secondary'>
          <div className={`h-full ${progressColor}`} style={{ width: `${progressPercent}%` }} />
        </div>
      )}

      {showMetadata && (
        <div className='mt-auto flex items-center justify-end gap-2'>
          {metric?.confidence && (
            <div className='rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground'>
              Confidence: {Math.round(metric.confidence * 100)}%
            </div>
          )}
          {metric?.justify && (
            <Popover
              trigger={
                <button
                  className='rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                  onClick={() => setShowReasoning(!showReasoning)}
                >
                  Show Reasoning
                </button>
              }
              className='p-3 text-xs text-muted-foreground'
            >
              {metric.justify}
            </Popover>
          )}
        </div>
      )}

      {isEditing && (
        <button
          onClick={() => setDialogOpen(true)}
          className='absolute bottom-3 left-3 inline-flex h-5 items-center gap-1 rounded-full border-[0.5px] px-2 py-0.5 text-[10px] hover:bg-muted/80'
        >
          <Pencil className='h-3 w-3' /> Update
        </button>
      )}

      <MetricOverrideDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        callId={callId}
        metric={metric}
        metricKey={metricKey}
      />
    </div>
  );
}

const PillContent = ({ result, isNA }: { result: MetricValue; isNA: boolean }) => {
  const metricStatus = useMemo(() => {
    if (!result) return null;
    return buildMetricStatus(result);
  }, [result]);

  if (metricStatus && !metricStatus.isEmpty) {
    switch (metricStatus.kind) {
      case 'number': {
        if (isNA) {
          return <span className='text-xs font-medium text-muted-foreground'>N/A</span>;
        }

        const s = metricStatus as NumberMetricStatus;
        const value = Math.round(s.value ?? 0);
        return (
          <span className='text-xs font-medium tabular-nums'>
            {value}/{s.max}
          </span>
        );
      }
      case 'boolean': {
        const b = metricStatus as BooleanMetricStatus;
        const val = b.truthy;
        return (
          <span className='inline-flex items-center gap-1 text-xs font-medium'>
            {val ? <Check className='h-3.5 w-3.5' /> : <X className='h-3.5 w-3.5' />}
            {val ? 'Yes' : 'No'}
          </span>
        );
      }
      case 'string': {
        const s = (metricStatus as StringMetricStatus).value || '—';
        return <span className='max-w-[7rem] truncate text-xs font-medium'>{s}</span>;
      }
      case 'list': {
        const list = (metricStatus as ListMetricStatus).value;
        if (list.length === 0) {
          return <span className='text-xs font-medium text-muted-foreground'>None</span>;
        } else if (list.length <= 2) {
          return (
            <div className='flex gap-1'>
              {list.map((t: string) => (
                <Badge key={t} variant='outline' className='px-1 py-0 text-[10px] font-normal'>
                  {t}
                </Badge>
              ))}
            </div>
          );
        } else {
          return <span className='text-xs font-medium'>{list.length} selected</span>;
        }
      }
    }
  }
  return <span className='text-xs font-medium text-muted-foreground'>—</span>;
};

function simpleStatusFor(status: ReturnType<typeof buildMetricStatus> | null): 'ok' | 'warn' | 'bad' | null {
  if (!status || status.isEmpty) return null;
  if (status.kind === 'number') {
    const sev = (status as NumberMetricStatus).severity; // low | medium | high
    if (sev === 'low') return 'bad';
    if (sev === 'medium') return 'warn';
    return 'ok';
  }
  if (status.kind === 'boolean') {
    const truthy = (status as BooleanMetricStatus).truthy;
    if (truthy == null) return null;
    return truthy ? 'ok' : 'bad';
  }
  return null;
}
